import { RuleSection } from '../types';

export const major06to10Sections: RuleSection[] = [
  {
    "id": "m06-overview",
    "title": "Turnieje i Godła: nominacje, korona i nagrody",
    "requiredModuleIds": [
      "major-06-jousts-crests"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-06",
      "corpus-base"
    ],
    "keywords": [
      "turniej",
      "godlo",
      "nagroda"
    ],
    "level": "intermediate",
    "hubGroup": "Turnieje i Godła",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Hrabia, Król i Rzeka.",
        "oldEditionName": "Hrabia, Król i Rzeka"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Turnieje i Godła nagradzają planowanie wokół własnych obszarów. Nominujesz zakryte godło, a gdy korona wskaże kategorię turnieju, porównujecie odkryte nominacje i rozdajecie kafelki nagród. Nagroda staje się miastem z posągiem i daje końcową premię zależną od herbów."
      },
      {
        "type": "image",
        "imageId": "joust-prizes",
        "alt": "Turniej i nagrody.",
        "caption": "Turnieje"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Nominacja", "Gdy przedłużasz własny obszar.", "Kładziesz zakryte godło, najwyżej jedno na turę."],
          ["Turniej", "Gdy żaden stos nie ma już nagrody na wierzchu.", "Korona wskazuje kategorię; rozdajecie nagrody."],
          ["Posąg", "Po zdobyciu kafelka nagrody.", "Na końcu: 3 × herby w tym mieście."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Mylenie tego dodatku z dawnym Hrabią, Królem i Rzeką. Korekta: stosuj wyłącznie procedury C3.1 Turniejów i Godła."
      },
      {
        "type": "crossReference",
        "sectionId": "m06-setup",
        "label": "Przygotowanie stosów i nagród"
      },
      {
        "type": "crossReference",
        "sectionId": "m06-nominate",
        "label": "Nominacje i rozstrzyganie turnieju"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Nominujesz godło na własnym obszarze.",
          "Turniej uruchamia się według korony i nagród na stosach.",
          "Nagroda staje się miastem z posągiem.",
          "Końcowa wartość: 3 × herby w tym mieście."
        ]
      },
      {
        "type": "qa",
        "question": "Czy nominacja jest zwykłą figurą na planszy?",
        "answer": "Nie. Nominacja to zakryte godło; nie zastępuje meepla i może zostać utracona, jeśli obszar ukończy się przed turniejem."
      }
    ],
    "imageIds": [
      "joust-prizes"
    ]
  },
  {
    "id": "m06-setup",
    "title": "Turnieje i Godła: przygotowanie stosów",
    "requiredModuleIds": [
      "major-06-jousts-crests"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-06",
      "corpus-base"
    ],
    "keywords": [
      "stosy",
      "korona"
    ],
    "level": "intermediate",
    "hubGroup": "Turnieje i Godła",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Hrabia, Król i Rzeka.",
        "oldEditionName": "Hrabia, Król i Rzeka"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Przed grą dzielisz kafelki krajobrazu na stosy zależne od liczby graczy i kładziesz na nich kafelki nagród. Korona będzie później wskazywać kategorię turnieju. Bez poprawnego przygotowania nie da się legalnie uruchomić nagród."
      },
      {
        "type": "numberedList",
        "items": [
          "Wtasuj kafelki turnieju do gry i przygotuj godła graczy.",
          "Podziel pozostałe kafelki krajobrazu na stosy według liczby graczy.",
          "Połóż kafelki nagród na stosach według tabeli poniżej.",
          "Ustaw koronę tak, aby później wskazywała kategorię turnieju."
        ]
      },
      {
        "type": "bulletList",
        "items": [
          "2–3 graczy: 6 stosów, 2 nagrody na stos",
          "4 graczy: 5 stosów, 2 nagrody na stos",
          "5 graczy: 4 stosy, 3 nagrody na stos",
          "6 graczy: 3 stosy, 3 nagrody na stos"
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Czterej gracze przygotowują Turnieje i Godła.",
            "Dzielą kafelki na 5 stosów z 2 nagrodami na każdym.",
            "1. Tasują kafelki. 2. Tworzą 5 stosów. 3. Kładą po 2 nagrody na stos. 4. Przygotowują godła i koronę.",
            "Partia ma poprawną strukturę nagród dla 4 graczy.",
            "Liczba stosów i nagród zależy od liczby graczy."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Użycie jednej wspólnej puli bez stosów nagród. Korekta: nagrody leżą na stosach i decydują, kiedy turniej może się uruchomić."
      },
      {
        "type": "crossReference",
        "sectionId": "m06-nominate",
        "label": "Kiedy turniej się uruchamia"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Liczba stosów zależy od liczby graczy.",
          "Na stosach leżą kafelki nagród.",
          "Korona wskazuje kategorię turnieju."
        ]
      },
      {
        "type": "qa",
        "question": "Czy przy 4 graczach robię 6 stosów?",
        "answer": "Nie. Przy 4 graczach przygotowujesz 5 stosów po 2 nagrody na każdym."
      }
    ]
  },
  {
    "id": "m06-nominate",
    "title": "Nominacje i rozstrzyganie turnieju",
    "requiredModuleIds": [
      "major-06-jousts-crests"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-06",
      "corpus-base"
    ],
    "keywords": [
      "nominacja"
    ],
    "level": "intermediate",
    "hubGroup": "Turnieje i Godła",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Hrabia, Król i Rzeka.",
        "oldEditionName": "Hrabia, Król i Rzeka"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Nominacja pozwala zaplanować przyszłą nagrodę. Gdy przedłużasz obszar z własnym podróżnikiem, rycerzem albo chłopem, możesz raz w turze położyć zakryte godło. Turniej następuje, gdy żaden stos nie ma już nagrody na wierzchu: korona wskazuje kategorię, odkrywacie odpowiednie godła, uwzględniacie premie i rozdajecie nagrody pierwszemu oraz drugiemu miejscu."
      },
      {
        "type": "numberedList",
        "items": [
          "Przedłuż obszar z własną figurą.",
          "Opcjonalnie połóż jedno zakryte godło jako nominację.",
          "Gdy warunek turnieju jest spełniony, korona wskazuje kategorię.",
          "Odkryjcie odpowiednie godła, dodajcie premie za płytki i sąsiedztwo, rozdajcie nagrody 1. i 2. miejscu."
        ]
      },
      {
        "type": "warning",
        "text": "Ukończenie obszaru przed turniejem może wycofać nominację. Nominacja nie jest trwałą figurą."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna przedłuża własne miasto z rycerzem i ma jeszcze wolne godło.",
            "Nominuje miasto zakrytym godłem.",
            "1. Przedłuża miasto. 2. Kładzie jedno zakryte godło. 3. Nie nominuje drugi raz w tej turze.",
            "Anna ma jedną aktywną nominację oczekującą na turniej.",
            "W turze wolno wykonać najwyżej jedną nominację."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Odkrywanie godła przed spełnieniem warunku turnieju. Korekta: godła pozostają zakryte aż do rozstrzygnięcia wskazanej kategorii."
      },
      {
        "type": "crossReference",
        "sectionId": "m06-scoring",
        "label": "Jak punktują zdobyte nagrody"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Nominacja: jedno zakryte godło na turę.",
          "Turniej uruchamia korona i brak nagród na wierzchu stosów.",
          "Ukończenie obszaru przed turniejem może usunąć nominację."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę nominować dwa obszary w jednej turze?",
        "answer": "Nie. W turze wolno wykonać najwyżej jedną nominację."
      }
    ]
  },
  {
    "id": "m06-scoring",
    "title": "Nagrody z posągiem i punktowanie końcowe",
    "requiredModuleIds": [
      "major-06-jousts-crests"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-06",
      "corpus-base"
    ],
    "keywords": [
      "posag",
      "herb"
    ],
    "level": "intermediate",
    "hubGroup": "Turnieje i Godła",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Hrabia, Król i Rzeka.",
        "oldEditionName": "Hrabia, Król i Rzeka"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Zwycięzca turnieju kładzie kafelek nagrody jako miasto z posągiem i zostawia na nim swoje godło. Na końcu gry każde takie godło daje trzy razy liczbę herbów w tym mieście, również nieukończonym. Liczą się herby miasta, nie liczba nominacji."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek wygrał turniej i ma posąg w mieście z dwoma herbami.",
            "Na końcu gry liczy godło na posągu.",
            "1. Potwierdza godło na posągu. 2. Liczy 2 herby. 3. Mnoży przez 3.",
            "Bartek otrzymuje 6 punktów.",
            "Końcowa wartość wynosi 3 × herby miasta z posągiem."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie liczby nominacji zamiast herbów w mieście z posągiem. Korekta: punkty zależą od herbów tego miasta."
      },
      {
        "type": "crossReference",
        "sectionId": "end-scoring",
        "label": "Miejsce nagród w punktowaniu końcowym"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Nagroda = miasto z posągiem i zachowanym godłem.",
          "Koniec gry: 3 × herby w tym mieście.",
          "Liczą się także nieukończone miasta z posągiem."
        ]
      },
      {
        "type": "qa",
        "question": "Czy nieukończone miasto z posągiem daje końcowe punkty za godło?",
        "answer": "Tak. Każde godło na posągu daje 3 × herby w tym mieście, ukończonym lub nie."
      }
    ]
  },
  {
    "id": "m07-overview",
    "title": "Oblężenie i Obrona: twierdza, stanowiska i katapulta",
    "requiredModuleIds": [
      "major-07-siege-defense"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-07",
      "corpus-base"
    ],
    "keywords": [
      "twierdza",
      "obrona",
      "katapulta"
    ],
    "level": "intermediate",
    "hubGroup": "Oblężenie i Obrona",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Katapulta.",
        "oldEditionName": "Katapulta"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Oblężenie i Obrona dodaje wspólny cel wokół twierdzy i trzech stanowisk obrony. Gracze mogą wysyłać figury do obrony, uruchamiać kontrataki i rozpatrywać ataki katapulty przed dołożeniem kafelka. Twierdza jest dużym kafelkiem wymagającym sześciu sąsiadów i liczy się jako ukończone miasto dla chłopów."
      },
      {
        "type": "image",
        "imageId": "fortress-defense",
        "alt": "Twierdza i stanowiska.",
        "caption": "Oblężenie"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy działa", "Najważniejszy skutek"],
        "rows": [
          ["Stanowisko obrony", "Dodatkowo w swojej turze.", "Wysyłasz figurę do rzędu; ostatnie miejsce uruchamia kontratak."],
          ["Symbol oblężenia", "Przed dołożeniem kafelka.", "Katapulta atakuje według zajętego stanowiska."],
          ["Twierdza", "Gdy ma sześciu sąsiadów.", "Jest ukończonym miastem dla farmerów."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Stosowanie zasad starej Katapulty C1/C2. Korekta: używaj wyłącznie procedur C3.1 Oblężenia i Obrony."
      },
      {
        "type": "crossReference",
        "sectionId": "m07-defense",
        "label": "Obrona i kontratak"
      },
      {
        "type": "crossReference",
        "sectionId": "m07-attack",
        "label": "Atak przed dołożeniem"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Każdy gracz dostaje 2 dodatkowe meeple.",
          "Obrona daje kontratak przy zapełnieniu rzędu.",
          "Atak katapulty działa przed dołożeniem.",
          "Twierdza liczy się jako ukończone miasto dla pól."
        ]
      },
      {
        "type": "qa",
        "question": "Czy twierdza jest zwykłym miastem do punktowania rycerzy?",
        "answer": "Twierdza jest dużym kafelkiem wymagającym sześciu sąsiadów i dla farmerów liczy się jak ukończone miasto; nie stosuj do niej zasad starej Katapulty."
      }
    ],
    "imageIds": [
      "fortress-defense"
    ]
  },
  {
    "id": "m07-defense",
    "title": "Obrona i kontratak: wysyłanie figurek do stanowisk",
    "requiredModuleIds": [
      "major-07-siege-defense"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-07",
      "corpus-base"
    ],
    "keywords": [
      "kontratak"
    ],
    "level": "intermediate",
    "hubGroup": "Oblężenie i Obrona",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Katapulta.",
        "oldEditionName": "Katapulta"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "W swojej turze możesz dodatkowo wysłać figurę do stanowiska obrony. Rycerz, podróżnik albo mnich/opat trafia do odpowiedniego rzędu, a chłop na pole meepla stanowiska. Gdy zajmiesz ostatnie wolne miejsce w rzędzie, uruchamiasz kontratak: zdobywasz 3 punkty za każdego własnego meepla w tym rzędzie, po czym figury wracają."
      },
      {
        "type": "numberedList",
        "items": [
          "W swojej turze wybierz figurę do wysłania na stanowisko.",
          "Połóż rycerza, podróżnika albo mnicha/opata w odpowiednim rzędzie, a chłopa na polu meepla.",
          "Jeśli zajęte zostało ostatnie miejsce, uruchom kontratak.",
          "Policz 3 punkty za każdego własnego meepla w tym rzędzie i zwróć figury."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma już dwie własne figury w rzędzie i zajmuje ostatnie miejsce trzecią.",
            "Uruchamia kontratak.",
            "1. Zajmuje ostatnie miejsce. 2. Liczy trzy własne figury. 3. Dostaje 9 punktów. 4. Figury wracają do zasobu.",
            "Anna zdobywa 9 punktów i odzyskuje figury.",
            "Kontratak daje 3 punkty za każdego własnego meepla w zapełnionym rzędzie."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pozostawienie figurek na stanowisku po kontrataku. Korekta: po kontrataku figury wracają do zasobów."
      },
      {
        "type": "crossReference",
        "sectionId": "m07-end",
        "label": "Końcowe kontrataki przed finałem"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Obrona to dodatkowa decyzja w turze.",
          "Ostatnie miejsce w rzędzie uruchamia kontratak.",
          "Kontratak: 3 × własne meeple, potem zwrot figurek."
        ]
      },
      {
        "type": "qa",
        "question": "Czy kontratak liczy też figury przeciwnika w rzędzie?",
        "answer": "Nie. Punkty liczy się tylko za własne meeple w tym rzędzie."
      }
    ]
  },
  {
    "id": "m07-attack",
    "title": "Atak katapulty przed dołożeniem kafelka",
    "requiredModuleIds": [
      "major-07-siege-defense"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-07",
      "corpus-base"
    ],
    "keywords": [
      "atak",
      "symbol"
    ],
    "level": "intermediate",
    "hubGroup": "Oblężenie i Obrona",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Katapulta.",
        "oldEditionName": "Katapulta"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Symbol oblężenia uruchamia katapultę przed dołożeniem kafelka. Jeżeli odpowiednie stanowisko jest zajęte, następuje atak. Gracz bez pasującej figury na tym stanowisku usuwa z mapy jedną pasującą figurę. Dopiero potem dokładasz kafelek i kontynuujesz turę."
      },
      {
        "type": "numberedList",
        "items": [
          "Dobierz kafelek z symbolem oblężenia, ale jeszcze go nie dokładasz.",
          "Sprawdź stanowisko wskazane przez atak katapulty.",
          "Jeśli stanowisko jest zajęte, rozpatrz atak: gracze bez pasującej figury usuwają jedną pasującą figurę z mapy.",
          "Dopiero potem dołóż kafelek i dokończ turę."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek dobrał kafelek z oblężeniem. Stanowisko rycerza jest zajęte, a Celina nie ma tam figury.",
            "Najpierw rozpatruje atak, potem dokłada kafelek.",
            "1. Wstrzymuje dołożenie. 2. Potwierdza zajęte stanowisko. 3. Celina usuwa jednego rycerza z mapy. 4. Bartek dokłada kafelek.",
            "Atak usunął figurę przed zmianą krajobrazu.",
            "Symbol oblężenia działa przed dołożeniem."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Uruchamianie oblężenia po dołożeniu kafelka. Korekta: atak katapulty rozpatruje się przed dołożeniem."
      },
      {
        "type": "crossReference",
        "sectionId": "turn-structure",
        "label": "Gdzie atak wchodzi w kolejność tury"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Symbol oblężenia działa przed dołożeniem.",
          "Zajęte stanowisko uruchamia atak.",
          "Gracz bez pasującej figury na stanowisku usuwa jedną pasującą figurę z mapy."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę najpierw dołożyć kafelek oblężenia, a potem ruszyć katapultą?",
        "answer": "Nie. Atak rozpatruje się przed dołożeniem kafelka."
      }
    ]
  },
  {
    "id": "m07-end",
    "title": "Oblężenie i Obrona na końcu gry",
    "requiredModuleIds": [
      "major-07-siege-defense"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-07",
      "corpus-base"
    ],
    "keywords": [
      "kontratak"
    ],
    "level": "intermediate",
    "hubGroup": "Oblężenie i Obrona",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Katapulta.",
        "oldEditionName": "Katapulta"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Przed zwykłym punktowaniem końcowym wykonaj po jednym kontrataku na każde stanowisko. Dzięki temu figury pozostałe na obronie jeszcze raz dają punkty i wracają. Twierdza liczy się jako ukończone miasto dla farmerów."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Przed finałem na stanowiskach pozostały figury Anny i Bartka.",
            "Wykonują po jednym kontrataku na stanowisko.",
            "1. Rozpatrują każde stanowisko. 2. Liczą 3 punkty za własne meeple. 3. Zwracają figury. 4. Przechodzą do zwykłego finału.",
            "Obrona zostaje rozliczona przed polami i innymi końcowymi punktami.",
            "Końcowe kontrataki są obowiązkowym krokiem przed standardowym finałem."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Pomijanie końcowych kontrataków. Korekta: przed finałem rozpatrz jedno kontratakowanie każdego stanowiska."
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Twierdza jako ukończone miasto dla pól"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Przed finałem: 1 kontratak na każde stanowisko.",
          "Twierdza = ukończone miasto dla chłopów.",
          "Nie stosuj zasad starej Katapulty."
        ]
      },
      {
        "type": "qa",
        "question": "Czy twierdza punktuje dla pól jak ukończone miasto?",
        "answer": "Tak. Dla farmerów twierdza liczy się jako ukończone miasto."
      }
    ]
  },
  {
    "id": "m08-overview",
    "title": "Zamki i Mosty: most, zamek i stodoła",
    "requiredModuleIds": [
      "major-08-castles-bridges"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-08",
      "corpus-base"
    ],
    "keywords": [
      "most",
      "zamek",
      "stodola"
    ],
    "level": "intermediate",
    "hubGroup": "Zamki i Mosty",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Mosty, Zamki i Bazary.",
        "oldEditionName": "Mosty, Zamki i Bazary"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Zamki i Mosty dają trzy konstrukcje ze wspólnej puli: most, zamek i stodołę. W jednej turze wolno zbudować najwyżej jedną z nich. Bazary z dawnego dodatku nie są używane. Każda konstrukcja zmienia sposób punktowania drogi, małego miasta albo pola."
      },
      {
        "type": "image",
        "imageId": "bridge-castle-barn",
        "alt": "Most, zamek, stodoła.",
        "caption": "Zamki i Mosty"
      },
      {
        "type": "table",
        "headers": ["Konstrukcja", "Kiedy", "Skutek"],
        "rows": [
          ["Most", "Łączy drogę ponad polem.", "Ukończona droga: 3 za kafelek z mostem; na końcu most = 1."],
          ["Zamek", "Miasto z dokładnie 2 segmentami.", "Zamiast punktów miasta powstaje dama."],
          ["Stodoła", "4 narożniki pola i twój chłop.", "Natychmiastowe punkty za farmerów na planszy."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Budowanie dwóch konstrukcji w jednej turze albo używanie zasad bazarów. Korekta: maksymalnie jedna konstrukcja na turę i bez bazarów."
      },
      {
        "type": "crossReference",
        "sectionId": "m08-bridge",
        "label": "Szczegółowo: mosty"
      },
      {
        "type": "crossReference",
        "sectionId": "m08-castle",
        "label": "Szczegółowo: zamki"
      },
      {
        "type": "crossReference",
        "sectionId": "m08-barn",
        "label": "Szczegółowo: stodoły"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Wspólna pula mostów, zamków i stodół.",
          "Najwyżej jedna konstrukcja na turę.",
          "Bez bazarów z dawnej edycji."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę w jednej turze zbudować most i zamek?",
        "answer": "Nie. W turze wolno zbudować najwyżej jedną konstrukcję z tego dodatku."
      }
    ],
    "imageIds": [
      "bridge-castle-barn"
    ]
  },
  {
    "id": "m08-bridge",
    "title": "Mosty: połączenie drogi i wartość 3",
    "requiredModuleIds": [
      "major-08-castles-bridges"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-08",
      "corpus-base"
    ],
    "keywords": [
      "most"
    ],
    "level": "intermediate",
    "hubGroup": "Zamki i Mosty",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Mosty, Zamki i Bazary.",
        "oldEditionName": "Mosty, Zamki i Bazary"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Most kładziesz nad polem według wzoru C3.1, aby połączyć drogę. Na ukończonej drodze kafelek z mostem jest wart 3 punkty zamiast 1. Jeżeli droga pozostanie nieukończona, na końcu gry most ma wartość 1."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna łączy dwa odcinki drogi mostem i później zamyka tę drogę o czterech kafelkach, z których jeden ma most.",
            "Liczy most jako 3 przy ukończeniu.",
            "1. Buduje most. 2. Zamyka drogę. 3. Liczy trzy zwykłe kafelki po 1 i most po 3.",
            "Anna otrzymuje 6 punktów za drogę.",
            "Ukończona droga daje 3 punkty za kafelek z mostem zamiast 1."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie mostu jako 3 także na końcu gry. Korekta: na końcu most jest wart 1."
      },
      {
        "type": "crossReference",
        "sectionId": "roads",
        "label": "Podstawowe punktowanie dróg"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Most łączy drogę ponad polem.",
          "Ukończona droga: 3 za kafelek z mostem.",
          "Na końcu most = 1."
        ]
      },
      {
        "type": "qa",
        "question": "Czy nieukończona droga z mostem daje 3 punkty za most na końcu?",
        "answer": "Nie. Na końcu gry most ma wartość 1."
      }
    ]
  },
  {
    "id": "m08-castle",
    "title": "Zamki i dama: zamiast punktowania małego miasta",
    "requiredModuleIds": [
      "major-08-castles-bridges"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-08",
      "corpus-base"
    ],
    "keywords": [
      "zamek",
      "dama"
    ],
    "level": "intermediate",
    "hubGroup": "Zamki i Mosty",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Mosty, Zamki i Bazary.",
        "oldEditionName": "Mosty, Zamki i Bazary"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Ukończone miasto z dokładnie dwoma segmentami może stać się zamkiem. Nie punktujesz wtedy miasta: kładziesz kafelek zamku, a zwykły meeple staje się damą. Przy odzyskaniu damy zdobywasz 1 punkt za nią oraz po 1 punkcie za każdego rycerza i każdą damę na planszy, wszystkich kolorów."
      },
      {
        "type": "numberedList",
        "items": [
          "Ukończ miasto mające dokładnie dwa segmenty.",
          "Zamiast punktować miasto połóż kafelek zamku.",
          "Zwykły meeple staje się damą.",
          "Gdy odzyskujesz damę, policz 1 za nią oraz 1 za każdego rycerza i damę na planszy."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek zamyka dwusegmentowe miasto z własnym rycerzem. Na planszy są łącznie 3 rycerze i 1 dama innych graczy.",
            "Buduje zamek zamiast punktować miasto.",
            "1. Nie liczy punktów miasta. 2. Kładzie zamek. 3. Meeple staje się damą. 4. Później przy odzyskaniu liczy 1 + 3 + 1.",
            "Bartek otrzymuje 5 punktów przy odzyskaniu damy.",
            "Zamek zastępuje natychmiastowe punktowanie małego miasta premią za damę."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie miasta i jednocześnie budowanie zamku. Korekta: zamek zastępuje punktowanie tego małego miasta."
      },
      {
        "type": "crossReference",
        "sectionId": "cities",
        "label": "Podstawowe ukończenie miast"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Zamek wymaga miasta z dokładnie 2 segmentami.",
          "Nie punktujesz miasta pod zamkiem.",
          "Odzyskanie damy: 1 + 1 za każdego rycerza i damę na planszy."
        ]
      },
      {
        "type": "qa",
        "question": "Czy przy odzyskaniu damy liczę tylko własne figury?",
        "answer": "Nie. Liczysz wszystkich rycerzy i damy na planszy, wszystkich kolorów."
      }
    ]
  },
  {
    "id": "m08-barn",
    "title": "Stodoły: natychmiastowe punkty za farmerów",
    "requiredModuleIds": [
      "major-08-castles-bridges"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-08",
      "corpus-base"
    ],
    "keywords": [
      "stodola"
    ],
    "level": "intermediate",
    "hubGroup": "Zamki i Mosty",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Mosty, Zamki i Bazary.",
        "oldEditionName": "Mosty, Zamki i Bazary"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Stodołę budujesz, gdy cztery narożniki tego samego pola i twój chłop na to pozwalają. Przenosisz chłopa na stodołę i natychmiast punktujesz po 1 punkcie za każdego farmera na planszy. Farmer na stodole nadal jest farmerem."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma chłopa na polu z czterema narożnikami. Na planszy są łącznie 4 farmerzy.",
            "Buduje stodołę i punktuje natychmiast.",
            "1. Sprawdza cztery narożniki. 2. Buduje stodołę. 3. Przenosi chłopa. 4. Dostaje 4 punkty.",
            "Celina otrzymuje 4 punkty, a jej farmer pozostaje na stodole.",
            "Stodoła daje natychmiast 1 punkt za każdego farmera na planszy."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Usuwanie farmera ze stodoły po natychmiastowym punktowaniu. Korekta: farmer przeniesiony na stodołę nadal jest farmerem."
      },
      {
        "type": "crossReference",
        "sectionId": "fields",
        "label": "Podstawowe zasady pól i chłopów"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Potrzeba 4 narożników tego samego pola i twojego chłopa.",
          "Natychmiast: 1 punkt za każdego farmera na planszy.",
          "Farmer na stodole pozostaje farmerem."
        ]
      },
      {
        "type": "qa",
        "question": "Czy stodoła usuwa chłopa z gry?",
        "answer": "Nie. Chłop przechodzi na stodołę i nadal jest farmerem."
      }
    ]
  },
  {
    "id": "m09-overview",
    "title": "Pasterze i Owce: ryzyko stada bez wzgórz",
    "requiredModuleIds": [
      "major-09-sheep-shepherds"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-09",
      "corpus-base"
    ],
    "keywords": [
      "pasterz",
      "owca",
      "wilk"
    ],
    "level": "intermediate",
    "hubGroup": "Pasterze i Owce",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wzgórza i Owce.",
        "oldEditionName": "Wzgórza i Owce"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Pasterze i Owce wprowadzają decyzję między ryzykiem losowania żetonu a bezpiecznym wypasem. Pasterz zajmuje pole zamiast chłopa, a winnice premiują klasztory i ogrody. Nie ma wzgórz z dawnej edycji i nie ma końcowego punktowania niewypasionych owiec."
      },
      {
        "type": "image",
        "imageId": "shepherd-flock",
        "alt": "Pasterz i stado.",
        "caption": "Pasterze"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy", "Skutek"],
        "rows": [
          ["Pasterz", "Zamiast meepla na polu.", "Losuje żeton; blokuje chłopów i drugiego pasterza."],
          ["Wypas", "Dobrowolnie lub gdy pole jest otoczone.", "Punkty za owce i gęsi; pasterz wraca."],
          ["Winnica", "Przy punktowaniu klasztoru/ogrodu.", "+3 za każdy sąsiedni kafelek winnicy."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Zostawianie niewypasionej owcy do punktowania końcowego. Korekta: owce i gęsi nie punktują na końcu, jeśli nie zostały wypasione."
      },
      {
        "type": "crossReference",
        "sectionId": "m09-shepherd",
        "label": "Pasterz i żetony stada"
      },
      {
        "type": "crossReference",
        "sectionId": "m09-graze",
        "label": "Wypas i gęsi"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Pasterz zajmuje pole zamiast chłopa.",
          "Wilk zeruje stado i zwraca pasterzy.",
          "Wypas daje punkty; niewypasione owce nie punktują na końcu.",
          "Bez wzgórz z dawnej edycji."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę mieć chłopa i pasterza na tym samym polu?",
        "answer": "Nie. Na polu z pasterzem nie wolno postawić chłopa ani drugiego pasterza."
      }
    ],
    "imageIds": [
      "shepherd-flock"
    ]
  },
  {
    "id": "m09-shepherd",
    "title": "Pasterz i stado: owce albo wilk",
    "requiredModuleIds": [
      "major-09-sheep-shepherds"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-09",
      "corpus-base"
    ],
    "keywords": [
      "pasterz"
    ],
    "level": "intermediate",
    "hubGroup": "Pasterze i Owce",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wzgórza i Owce.",
        "oldEditionName": "Wzgórza i Owce"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Pasterza wystawiasz zamiast meepla na wolnym polu bez innego pasterza. Po wystawieniu lub przy ryzyku losujesz żeton stada. Owca zostaje przy pasterzu. Wilk zwraca bez punktów wszystkie żetony i pasterzy z połączonego pola."
      },
      {
        "type": "numberedList",
        "items": [
          "Wystaw pasterza na legalnym polu zamiast zwykłego meepla.",
          "Wylosuj żeton stada.",
          "Owca: połóż ją przy pasterzu.",
          "Wilk: zwróć wszystkie żetony i pasterzy z połączonego pola bez punktów."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna ma pasterza z owcami o wartości 5. Losuje wilka.",
            "Rozpatruje wilka natychmiast.",
            "1. Potwierdza połączone pole. 2. Zwraca wszystkie żetony. 3. Zwraca wszystkich pasterzy z tego pola. 4. Nie przyznaje punktów.",
            "Stado znika bez punktów.",
            "Wilk zeruje całe połączone pole, nie tylko aktywnego gracza."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Traktowanie wilka jako kary tylko dla aktywnego gracza. Korekta: wracają wszystkie żetony i pasterze z połączonego pola."
      },
      {
        "type": "crossReference",
        "sectionId": "m09-graze",
        "label": "Kiedy wypasać zamiast ryzykować"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Pasterz zamiast meepla na polu.",
          "Owca zostaje przy pasterzu.",
          "Wilk zwraca stado i pasterzy bez punktów."
        ]
      },
      {
        "type": "qa",
        "question": "Czy wilk zabiera punkty już zdobyte wcześniej?",
        "answer": "Nie. Wilk usuwa bieżące żetony i pasterzy z pola; nie cofa wcześniej wypasionych punktów."
      }
    ]
  },
  {
    "id": "m09-graze",
    "title": "Wypas i gęsi: kiedy zabezpieczyć wynik",
    "requiredModuleIds": [
      "major-09-sheep-shepherds"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-09",
      "corpus-base"
    ],
    "keywords": [
      "wypas",
      "gesi"
    ],
    "level": "intermediate",
    "hubGroup": "Pasterze i Owce",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wzgórza i Owce.",
        "oldEditionName": "Wzgórza i Owce"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Gdy przedłużasz pole z pasterzem, wybierasz ryzyko losowania albo natychmiastowy wypas. Całkowicie otoczone pole wymusza wypas. Przy wypasie każdy pasterz na polu dostaje pełną wartość owiec, a aktywny pasterz dodatkowo po 1 punkcie za każdy kafelek z gęsiami sąsiadujący bokiem lub narożnikiem."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Bartek ma owce o wartości 5 i dwa sąsiednie kafelki gęsi przy pasterzu.",
            "Wybiera dobrowolny wypas.",
            "1. Przedłuża pole. 2. Wybiera wypas zamiast ryzyka. 3. Dostaje 5 za owce i 2 za gęsi. 4. Pasterz wraca.",
            "Bartek otrzymuje 7 punktów.",
            "Wypas daje pełną wartość owiec oraz +1 za każdy sąsiadujący kafelek gęsi."
          ]
        ]
      },
      {
        "type": "warning",
        "text": "Owce i gęsi nie punktują na końcu, jeśli nie zostały wypasione."
      },
      {
        "type": "commonMistake",
        "text": "Zostawianie niewypasionej owcy do finału. Korekta: bez wypasu nie ma końcowych punktów za owce ani gęsi."
      },
      {
        "type": "crossReference",
        "sectionId": "m09-vineyards",
        "label": "Winnice przy klasztorach i ogrodach"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Przy przedłużeniu: ryzyko albo wypas.",
          "Otoczone pole = obowiązkowy wypas.",
          "Aktywny pasterz: +1 za każdy sąsiadujący kafelek gęsi."
        ]
      },
      {
        "type": "qa",
        "question": "Czy wszyscy pasterze na polu dostają punkty za gęsi?",
        "answer": "Pełną wartość owiec dostaje każdy pasterz na polu; premię za gęsi otrzymuje aktywny pasterz."
      }
    ]
  },
  {
    "id": "m09-vineyards",
    "title": "Winnice: +3 do klasztoru lub ogrodu",
    "requiredModuleIds": [
      "major-09-sheep-shepherds"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-09",
      "corpus-base"
    ],
    "keywords": [
      "winnica"
    ],
    "level": "intermediate",
    "hubGroup": "Pasterze i Owce",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Wzgórza i Owce.",
        "oldEditionName": "Wzgórza i Owce"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Winnica nie punktuje sama. Przy punktowaniu klasztoru albo ogrodu dodajesz +3 za każdy kafelek winnicy sąsiadujący bokiem lub narożnikiem. Same winnice nie dają punktów na końcu gry i nie tworzą osobnego obszaru większości."
      },
      {
        "type": "heading",
        "text": "Kiedy i jak stosować",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Premię liczysz w momencie punktowania klasztoru lub ogrodu — zarówno przy pełnym ukończeniu (9 punktów), jak i przy wcześniejszym wycofaniu Opata albo punktowaniu końcowym nieukończonego klasztoru/ogrodu. Najpierw ustal podstawową wartość obszaru, potem dodaj +3 za każdą sąsiadującą winnicę."
      },
      {
        "type": "numberedList",
        "items": [
          "Ustal, że punktujesz klasztor lub ogród.",
          "Policz podstawową wartość (9 albo 1 + obecni sąsiedzi).",
          "Znajdź wszystkie kafelki winnicy stykające się bokiem lub narożnikiem z kafelkiem klasztoru/ogrodu.",
          "Dodaj +3 za każdą taką winnicę do wyniku gracza kontrolującego obszar."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina punktuje ukończony klasztor sąsiadujący z dwoma winnicami.",
            "Dodaje premię winnic do 9 punktów klasztoru.",
            "1. Klasztor daje 9. 2. Liczy dwie sąsiadujące winnice. 3. Dodaje 2 × 3.",
            "Celina otrzymuje 15 punktów.",
            "Każda sąsiadująca winnica daje +3 przy punktowaniu klasztoru lub ogrodu."
          ]
        ]
      },
      {
        "type": "exception",
        "text": "Winnica nie premiuje dróg, miast ani pól. Nie daje też punktów, gdy leży samotnie bez punktowanego klasztoru lub ogrodu."
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie samotnych winnic na końcu gry. Korekta: winnice dają premię tylko przy punktowaniu klasztoru lub ogrodu."
      },
      {
        "type": "crossReference",
        "sectionId": "monasteries",
        "label": "Podstawowe punktowanie klasztoru"
      },
      {
        "type": "crossReference",
        "sectionId": "abbot-rules",
        "label": "Ogrody i Opat"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Winnica: +3 przy klasztorze lub ogrodzie.",
          "Liczą się sąsiedzi bokiem i narożnikiem.",
          "Same winnice nie punktują na końcu.",
          "Najpierw wartość klasztoru/ogrodu, potem premia."
        ]
      },
      {
        "type": "qa",
        "question": "Czy winnica daje punkty bez klasztoru lub ogrodu?",
        "answer": "Nie. Premia pojawia się tylko przy punktowaniu klasztoru albo ogrodu."
      },
      {
        "type": "qa",
        "question": "Czy winnica premiuje także wczesne wycofanie Opata?",
        "answer": "Tak. Najpierw liczysz bieżącą wartość klasztoru lub ogrodu (1 + obecni sąsiedzi), a następnie dodajesz +3 za każdą sąsiadującą winnicę."
      }
    ]
  },
  {
    "id": "m10-overview",
    "title": "Cyrk i Artyści: występy, piramida i dyrektorka",
    "requiredModuleIds": [
      "major-10-circus-artists"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-10",
      "corpus-base"
    ],
    "keywords": [
      "cyrk",
      "akrobata"
    ],
    "level": "intermediate",
    "hubGroup": "Cyrk i Artyści",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Cyrk Objazdowy / Under the Big Top.",
        "oldEditionName": "Cyrk Objazdowy / Under the Big Top"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Cyrk i Artyści nagradza przygotowanie figurek wokół placów cyrkowych. Występ rozlicza się po zwykłym punktowaniu, piramida akrobatów czeka na świadomą decyzję, a dyrektorka premiuje sąsiedztwo placów. Nie używaj zasad starszego Cyrku Objazdowego."
      },
      {
        "type": "image",
        "imageId": "circus-acrobats",
        "alt": "Cyrk i akrobaci.",
        "caption": "Cyrk"
      },
      {
        "type": "table",
        "headers": ["Element", "Kiedy", "Skutek"],
        "rows": [
          ["Występ", "Po dołożeniu placu i zwykłym punktowaniu.", "Wartość żetonu za meeple na kafelku i 8 sąsiadach; meeple nie wracają."],
          ["Piramida", "Do 3 akrobatów na placu.", "Punktuje 5 za akrobata, gdy poświęcisz późniejsze wystawienie."],
          ["Dyrektorka", "Siła 1 na obszarze.", "+2 za każdy plac cyrkowy/akrobatyczny na jej kafelku i 8 sąsiadach."]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Zwracanie meepli po występie. Korekta: meeple punktujące występ pozostają na planszy."
      },
      {
        "type": "crossReference",
        "sectionId": "m10-show",
        "label": "Procedura występu"
      },
      {
        "type": "crossReference",
        "sectionId": "m10-acrobats",
        "label": "Piramida akrobatów"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Występ następuje po zwykłym punktowaniu.",
          "Piramida nie punktuje automatycznie.",
          "Na końcu każdy akrobata daje 5.",
          "Nie stosuj zasad starszego Cyrku Objazdowego."
        ]
      },
      {
        "type": "qa",
        "question": "Czy występ wraca meeple do zasobu?",
        "answer": "Nie. Meeple, za które liczysz występ, nie wracają."
      }
    ],
    "imageIds": [
      "circus-acrobats"
    ]
  },
  {
    "id": "m10-show",
    "title": "Występ: opóźniona nagroda wokół namiotu",
    "requiredModuleIds": [
      "major-10-circus-artists"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-10",
      "corpus-base"
    ],
    "keywords": [
      "namiot",
      "wystep"
    ],
    "level": "intermediate",
    "hubGroup": "Cyrk i Artyści",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Cyrk Objazdowy / Under the Big Top.",
        "oldEditionName": "Cyrk Objazdowy / Under the Big Top"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Po dołożeniu placu cyrkowego najpierw rozstrzygasz zwykłe ukończenia. Potem kładziesz żeton występu i przesuwasz namiot; odkryty poprzedni żeton uruchamia występ. Każdy gracz dostaje wartość żetonu za każdego swojego meepla na kafelku występu i jego ośmiu sąsiadach. Te meeple nie wracają."
      },
      {
        "type": "resolutionOrder",
        "title": "Po placu cyrkowym",
        "steps": [
          "Rozpatrz zwykłe punktowanie ukończeń.",
          "Połóż żeton występu na placu i przesuń namiot.",
          "Odkryj poprzedni żeton i rozdaj punkty występu."
        ]
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna dokłada plac. Wokół poprzedniego namiotu stoi jej meeple i meeple Bartka.",
            "Po zwykłym punktowaniu rozpatruje występ.",
            "1. Kończy zwykłe ukończenia. 2. Kładzie nowy żeton i przesuwa namiot. 3. Odkrywa poprzedni żeton. 4. Każdy dostaje wartość żetonu za swojego meepla.",
            "Oboje punktują występ, a ich meeple zostają.",
            "Występ jest opóźnioną nagrodą i nie zwraca figurek."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Punktowanie występu przed zwykłymi ukończeniami. Korekta: najpierw zwykłe punktowanie, potem występ."
      },
      {
        "type": "crossReference",
        "sectionId": "mid-scoring",
        "label": "Zwykłe punktowanie przed występem"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Najpierw zwykłe punktowanie, potem występ.",
          "Liczą się meeple na kafelku i 8 sąsiadach.",
          "Meeple nie wracają po występie."
        ]
      },
      {
        "type": "qa",
        "question": "Czy mogę zdjąć meepla zaraz po otrzymaniu punktów z występu?",
        "answer": "Nie. Meeple punktujące występ pozostają na planszy."
      }
    ]
  },
  {
    "id": "m10-acrobats",
    "title": "Akrobaci i piramida: 5 punktów za decyzję",
    "requiredModuleIds": [
      "major-10-circus-artists"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-10",
      "corpus-base"
    ],
    "keywords": [
      "piramida"
    ],
    "level": "intermediate",
    "hubGroup": "Cyrk i Artyści",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Cyrk Objazdowy / Under the Big Top.",
        "oldEditionName": "Cyrk Objazdowy / Under the Big Top"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Na placu może powstać piramida do trzech akrobatów, także różnych graczy. Piramida nie punktuje automatycznie. W późniejszej turze możesz, zamiast wystawienia figury, zapunktować pełną piramidę zawierającą twojego akrobata: 5 punktów za każdego akrobata. Na końcu gry każdy pozostały akrobata daje 5."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Celina ma akrobata w pełnej piramidzie trzech figurek.",
            "W późniejszej turze rezygnuje z wystawienia figury i punktuje piramidę.",
            "1. Potwierdza pełną piramidę. 2. Rezygnuje z wystawienia. 3. Liczy 3 × 5.",
            "Celina otrzymuje 15 punktów.",
            "Pełna piramida z twoim akrobatą punktuje dopiero po świadomej decyzji zamiast wystawienia."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Automatyczne punktowanie niepełnej piramidy. Korekta: punktujesz tylko pełną piramidę i tylko zamiast wystawienia figury albo na końcu po 5 za akrobata."
      },
      {
        "type": "crossReference",
        "sectionId": "meeple-placement",
        "label": "Wystawienie figury jako koszt punktowania piramidy"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Do 3 akrobatów na placu.",
          "Pełna piramida: 5 za akrobata zamiast wystawienia.",
          "Na końcu każdy akrobata daje 5."
        ]
      },
      {
        "type": "qa",
        "question": "Czy piramida punktuje sama, gdy dołożę trzeciego akrobata?",
        "answer": "Nie automatycznie. Pełną piramidę punktujesz później, rezygnując z wystawienia figury, albo na końcu gry po 5 za akrobata."
      }
    ]
  },
  {
    "id": "m10-ringmistress",
    "title": "Dyrektorka: siła 1 i premia za place",
    "requiredModuleIds": [
      "major-10-circus-artists"
    ],
    "editionTag": "C3.1",
    "sourceIds": [
      "official-10",
      "corpus-base"
    ],
    "keywords": [
      "dyrektorka"
    ],
    "level": "intermediate",
    "hubGroup": "Cyrk i Artyści",
    "content": [
      {
        "type": "editionWarning",
        "text": "Zasady C3.1 (2025). Nie stosuj reguł z: Cyrk Objazdowy / Under the Big Top.",
        "oldEditionName": "Cyrk Objazdowy / Under the Big Top"
      },
      {
        "type": "heading",
        "text": "Czego nauczysz się w tym rozdziale",
        "level": 2
      },
      {
        "type": "paragraph",
        "text": "Dyrektorka ma siłę 1 i może stać na drodze, w mieście, klasztorze albo na polu. Przy punktowaniu jej obszaru dodajesz +2 za każdy plac cyrkowy lub akrobatyczny na jej kafelku oraz na ośmiu sąsiadach."
      },
      {
        "type": "table",
        "headers": ["Stan", "Decyzja", "Kroki", "Wynik", "Dlaczego jest poprawny"],
        "rows": [
          [
            "Anna punktuje drogę dyrektorki. Wokół jej kafelka są dwa place cyrkowe.",
            "Dodaje premię placów do zwykłych punktów drogi.",
            "1. Liczy zwykłą wartość drogi. 2. Sprawdza kafelek dyrektorki i 8 sąsiadów. 3. Dodaje 2 × 2.",
            "Anna otrzymuje zwykłe punkty drogi oraz 4 punkty premii.",
            "Dyrektorka daje +2 za każdy plac cyrkowy lub akrobatyczny w swoim otoczeniu."
          ]
        ]
      },
      {
        "type": "commonMistake",
        "text": "Liczenie premii dyrektorki od wszystkich placów na całej mapie. Korekta: liczą się tylko plac na jej kafelku i ośmiu sąsiadach."
      },
      {
        "type": "crossReference",
        "sectionId": "majority-ties",
        "label": "Siła 1 dyrektorki w większości"
      },
      {
        "type": "heading",
        "text": "Krótko",
        "level": 2
      },
      {
        "type": "bulletList",
        "items": [
          "Dyrektorka ma siłę 1.",
          "Może stać na drodze, mieście, klasztorze lub polu.",
          "Przy punktowaniu: +2 za każdy pobliski plac cyrkowy/akrobatyczny."
        ]
      },
      {
        "type": "qa",
        "question": "Czy dyrektorka może stać na polu?",
        "answer": "Tak. Może zajmować drogę, miasto, klasztor albo pole i ma siłę 1."
      }
    ]
  }
];
